import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Users, Lock } from "lucide-react";

export default function PartnerTypes() {
  const [types, setTypes] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const load = useCallback(() => api.get("/partner-types").then((r) => setTypes(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    try { await api.post("/partner-types", { label: newLabel.trim() }); toast.success("Partner type added"); setNewLabel(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveEdit = async (key) => {
    try { await api.put(`/partner-types/${key}`, { label: editLabel }); toast.success("Renamed"); setEditKey(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (key) => {
    if (!window.confirm("Delete this partner type?")) return;
    try { await api.delete(`/partner-types/${key}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Partner Types</h1>
        <p className="text-sm text-slate-500 mt-1">Define the partner categories available across the platform</p>
      </div>

      <form onSubmit={add} className="flex gap-3">
        <input data-testid="partner-type-name-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New type (e.g. Connector, Referral Partner)" className={`${inputCls} max-w-sm`} />
        <button data-testid="add-partner-type-button" type="submit" className={btnPrimary}><Plus size={16} /> Add Type</button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.map((t) => (
          <div key={t.key} data-testid={`partner-type-card-${t.key}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"><Users size={20} /></div>
                {editKey === t.key ? (
                  <input data-testid={`partner-type-edit-input-${t.key}`} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className={inputCls} autoFocus />
                ) : (
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 font-heading truncate flex items-center gap-1.5">{t.label}{t.builtin && <Lock size={12} className="text-slate-400" />}</div>
                    <div className="text-xs text-slate-400 font-mono">{t.key}</div>
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {editKey === t.key ? (
                  <>
                    <button data-testid={`save-partner-type-${t.key}`} onClick={() => saveEdit(t.key)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><Check size={16} /></button>
                    <button onClick={() => setEditKey(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <button data-testid={`edit-partner-type-${t.key}`} onClick={() => { setEditKey(t.key); setEditLabel(t.label); }} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                    {!t.builtin && <button data-testid={`delete-partner-type-${t.key}`} onClick={() => del(t.key)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
